import { startApplianceUpdate, isUpdateActive } from "../components/ApplianceUpdateGate";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Copy, ExternalLink, RefreshCcw, ServerCog, ShieldAlert } from "lucide-react";
import { api, formatDate } from "../lib/api";
import { EnvironmentSettings } from "../components/EnvironmentSettings";
import { offeredUpdateProgress } from "../lib/appliance-update-state";
import { SystemDataSettings } from "../components/SystemDataSettings";

export type ApplianceStatus = {
  enabled: boolean;
  instanceId: string;
  version: string;
  channel: "stable" | "candidate";
  publicIp: string | null;
  status: "ACTIVE" | "GRACE" | "SUSPENDED" | "REVOKED" | "UPDATE_REQUIRED" | "PENDING";
  checkedAt: string;
  leaseExpiresAt: string | null;
  graceExpiresAt: string | null;
  reason: string | null;
  controlReachable: boolean;
  commercialOperationsAllowed: boolean;
  hermesDashboardUrl: string | null;
  hermesPublicAuthReady: boolean;
  update: {
    available: boolean;
    version: string | null;
    releaseNotes: string | null;
    publishedAt: string | null;
    required: boolean;
    requiresRestart: boolean;
  };
  updateInstallation: {
    state: string;
    targetVersion: string | null;
    progress: number;
    error: string | null;
    updatedAt: string | null;
    completedAt: string | null;
  };
};

const statusLabel: Record<ApplianceStatus["status"], string> = {
  ACTIVE: "Activa",
  GRACE: "Período de gracia",
  SUSPENDED: "Suspendida",
  REVOKED: "Revocada",
  UPDATE_REQUIRED: "Actualización requerida",
  PENDING: "Verificación pendiente",
};

export function SystemPage() {
  const [tunnelCopied, setTunnelCopied] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const appliance = useQuery({
    queryKey: ["system-appliance"],
    queryFn: () => api<ApplianceStatus>("/system/appliance"),
    refetchInterval: 30_000,
  });
  if (appliance.isPending) return <div className="loading">Leyendo estado del Appliance…</div>;
  if (appliance.isError) return <div className="query-state error"><strong>No pudimos leer el estado del sistema</strong><button className="secondary" onClick={() => void appliance.refetch()}><RefreshCcw size={16} />Reintentar</button></div>;
  const state = appliance.data;
  const healthy = state.status === "ACTIVE";
  const updateRunning = isUpdateActive(state.updateInstallation.state);
  const offered = offeredUpdateProgress(state.update.version, state.updateInstallation);
  const hermesTunnelCommand = state.publicIp ? `ssh -N -L 9119:127.0.0.1:9119 root@${state.publicIp}` : null;
  function copyWithTemporaryField(value: string) {
    const field = document.createElement("textarea");
    field.value = value;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    document.execCommand("copy");
    field.remove();
  }
  async function copyHermesTunnel() {
    if (!hermesTunnelCommand) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(hermesTunnelCommand);
    } catch {
      copyWithTemporaryField(hermesTunnelCommand);
    }
    setTunnelCopied(true);
    window.setTimeout(() => setTunnelCopied(false), 2_000);
  }
  return <div className="system-page">
    <div className="detail-head"><div><span className="eyebrow">VICNET APPLIANCE</span><h1>Sistema y actualizaciones</h1><p>Estado verificable de esta instalación, Hermes y el canal privado de versiones.</p></div><button className="secondary" disabled={appliance.isFetching} onClick={() => void appliance.refetch()}><RefreshCcw className={appliance.isFetching ? "spin" : undefined} size={16} />Actualizar estado</button></div>

    <section className={`panel appliance-state ${healthy ? "healthy" : "attention"}`}>
      <header><div>{healthy ? <CheckCircle2 /> : <ShieldAlert />}<span><small>Estado de instancia</small><strong>{statusLabel[state.status]}</strong></span></div><span className={`status ${healthy ? "status-success" : "status-warning"}`}>{state.status}</span></header>
      {state.reason && <p>{state.reason}</p>}
      <dl className="system-facts">
        <div><dt>Versión instalada</dt><dd>{state.version}</dd></div>
        <div><dt>Canal</dt><dd>{state.channel}</dd></div>
        <div><dt>Instancia</dt><dd><code>{state.instanceId}</code></dd></div>
        <div><dt>Control central</dt><dd>{state.controlReachable ? "Conectado" : state.enabled ? "Sin conexión reciente" : "No administrada"}</dd></div>
        <div><dt>Última comprobación</dt><dd>{formatDate(state.checkedAt)}</dd></div>
        <div><dt>Operación comercial</dt><dd>{state.commercialOperationsAllowed ? "Habilitada" : "Solo lectura"}</dd></div>
      </dl>
    </section>

    <div className="system-grid">
      <section className="panel update-card">
        <span className="eyebrow">ACTUALIZACIONES</span>
        <h2>{state.update.available ? `Versión ${state.update.version} disponible` : "La instalación está actualizada"}</h2>
        {state.update.available ? <>
          <p>{state.update.releaseNotes || "Hay una nueva versión aprobada para esta instancia."}</p>
          <div className="update-flags">{state.update.required && <span><AlertTriangle size={15} />Obligatoria</span>}<span>{state.update.requiresRestart ? "Requiere reinicio controlado" : "Sin reinicio"}</span>{state.update.publishedAt && <span>Publicada {formatDate(state.update.publishedAt)}</span>}</div>
          <div className="update-progress"><span style={{ width: `${offered.progress}%` }} /><small>{offered.label}{offered.progress > 0 ? ` · ${offered.progress}%` : ""}</small></div>
          {offered.error && <p className="form-error">{offered.error}</p>}
          <p>Al iniciar se bloqueará la interfaz. Habrá un respaldo, reinicio temporal de servicios y reconexión automática.</p>
          <button className="primary" disabled={updateRunning} onClick={() => setConfirmUpdate(true)}>{updateRunning ? "Actualizando…" : "Instalar actualización"}</button>
        </> : <p>El control central no publicó una versión más reciente para este canal.</p>}
      </section>

      <section className="panel hermes-system-card">
        <span className="eyebrow">HERMES</span><h2>Panel de agentes</h2>
        <p>Los nueve perfiles quedan configurados con OpenAI/Codex. En el primer acceso, vinculá una cuenta una sola vez desde Claves; Hermes comparte esa autorización con todos los perfiles.</p>
        {state.hermesDashboardUrl && state.hermesPublicAuthReady
          ? <a className="secondary button-link" href={state.hermesDashboardUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} />Abrir panel de Hermes</a>
          : <div className="hermes-private-access">
            <span className="muted"><ServerCog size={16} />Acceso privado por túnel SSH</span>
            {hermesTunnelCommand
              ? <div className="hermes-tunnel-command"><code>{hermesTunnelCommand}</code><button className="secondary" type="button" onClick={() => void copyHermesTunnel()}>{tunnelCopied ? <Check size={15} /> : <Copy size={15} />}{tunnelCopied ? "Copiado" : "Copiar comando"}</button></div>
              : <span className="muted">Consultá el comando exacto con <code>sudo vicnet-appliance hermes-access</code>.</span>}
            <span className="muted">Ejecutalo en tu computadora y dejá esa terminal abierta. Después abrí <a href="http://127.0.0.1:9119" target="_blank" rel="noreferrer">http://127.0.0.1:9119</a>.</span>
          </div>}
      </section>
    </div>
    <SystemDataSettings instanceId={state.instanceId} disabled={updateRunning} />
    <EnvironmentSettings disabled={updateRunning} />
    {confirmUpdate && <div className="update-lock" role="dialog" aria-modal="true" aria-labelledby="update-confirm-title"><section className="panel update-lock-card"><h2 id="update-confirm-title">¿Instalar la versión {state.update.version}?</h2><p>Se creará un respaldo y se reiniciarán los servicios. La aplicación puede dejar de responder temporalmente y la interfaz quedará bloqueada hasta confirmar el resultado.</p><div className="actions"><button className="secondary" onClick={() => setConfirmUpdate(false)}>Cancelar</button><button className="primary" disabled={updateRunning} onClick={(event) => { event.currentTarget.disabled = true; setConfirmUpdate(false); startApplianceUpdate(state.update.version!); }}>Confirmar actualización</button></div></section></div>}
  </div>;
}
